export type JobsApiItem = {
  id: string;
  title: string;
  description: string;
  company: string;
  location: string;
  url: string;
  posted_date: string;
  aggregator_name: string;
  salary_min: number | null;
  salary_max: number | null;
  salary_curr: string | null;
  cost_model_cpa: number | null;
  cost_model_cpc: number | null;
  cost_model_rph: number | null;
  cpa_value: number | null;
  cpc_value: number | null;
  rph_value: number | null;
  country: string | null;
  city: string | null;
  state: string | null;
  ref_id: string | null;
  aggregator_id: string | null;
  category: string[];
  job_type: string | null;
  skills: string[];
  keywords: string[];
  logo: string;
  feed_id: string;
};

export type JobsApiResponse = {
  items: JobsApiItem[];
  total: number;
  limit: number;
  offset: number;
  has_more: boolean;
};

export const JOB_ALERT_FREQUENCIES = ["daily", "weekly"] as const;

export type JobAlertFrequency = (typeof JOB_ALERT_FREQUENCIES)[number];

export const JOB_ALERT_EXPERIENCES = [
  "internship",
  "fresher",
  "1-3_years",
  "3-5_years",
  "5-7_years",
  "7-10_years",
  "10+_years",
] as const;

export type JobAlertExperience = (typeof JOB_ALERT_EXPERIENCES)[number];

export type CompactJobAlertJob = {
  title: string;
  url: string;
  location: string;
  postedDate: string;
  companyName: string;
  dedupeKey: string;
  experience?: string;
  jobType?: string;
  workMode?: string;
};

export type CompactJobAlertJobsRequest = {
  creatorId: string;
  experience: JobAlertExperience;
  postedAfter: string;
  limit: number;
  subscriberTimezone?: string;
};

export type CompactJobAlertJobsResponse = {
  jobs: CompactJobAlertJob[];
};

export const COUNTRY_CODE = {
  US: "US",
  IN: "IN",
} as const;

export type CountryCode = (typeof COUNTRY_CODE)[keyof typeof COUNTRY_CODE];

export const JOB_PROVIDER = {
  JOOBLE: "jooble",
  JOBRAPIDO: "jobrapido",
  WHATJOB: "whatjob",
  WHATJOBS: "whatjobs",
  ADZUNA: "adzuna",
  APPCAST: "appcast",
  TALROO: "talroo",
  CPA: "cpa",
} as const;

export type JobProvider = (typeof JOB_PROVIDER)[keyof typeof JOB_PROVIDER];

export interface UpsertPublicSubscriptionInput {
  creatorId: string;
  creatorUsername: string;
  email: string;
  frequency?: JobAlertFrequency;
  experience?: JobAlertExperience;
  timezone?: string;
  nextSendAt?: Date | null;
  externalId?: string;
  posthogDistinctId?: string;
}

export interface CandidateJobAlert {
  externalId: string;
  creatorId: string;
  creatorUsername: string;
  email: string;
  frequency?: JobAlertFrequency;
  experience?: JobAlertExperience;
  isActive: boolean;
  unsubscribeAt: Date | null;
  subscribedAt: Date;
  timezone?: string;
  nextSendAt: Date | null;
  createdAt: Date;
  updatedAt: Date;
}

export interface CandidateDerivedPreferences {
  titles: string[];
  skills: string[];
  keywords: string[];
  location?: {
    city?: string;
    state?: string;
    country?: string;
  };
  experienceRange?: {
    min: number;
    max: number;
  };
  experienceYears?: number;
  education?: Array<{
    level?: string;
    field?: string;
  }>;
  workMode?: string;
}

export interface JobAlertSubscriptionRecord {
  candidateExternalId?: string;
  creatorId: string;
  creatorUsername: string;
  email: string;
  externalId: string;
  frequency?: JobAlertFrequency;
  experience?: JobAlertExperience;
  isActive: boolean;
  unsubscribeAt: Date | null;
  subscribedAt: Date;
  timezone?: string;
  nextSendAt: Date | null;
  createdAt: Date;
  updatedAt: Date;
}

export interface PublicJobAlertStorage {
  externalId: string | null;
  frequency: JobAlertFrequency;
  experience: JobAlertExperience;
}

export interface PublicJobAlertFeedback {
  cardId: string | null;
  errorMessage: string | null;
  successMessage: string | null;
}

export interface PublicJobAlertSubscriptionController<TResponse = any> {
  email: string;
  frequency: JobAlertFrequency;
  experience: JobAlertExperience;
  isSubmitting: boolean;
  feedback: PublicJobAlertFeedback;
  setEmail: (value: string) => void;
  setFrequency: (value: JobAlertFrequency) => void;
  setExperience: (value: JobAlertExperience) => void;
  externalId: string | null;
  isModalOpen: boolean;
  activeModalCardId: string | null;
  setActiveModalCardId: (value: string | null) => void;
  submit: (cardId: string) => Promise<TResponse | null>;
}
